<template>
  <div>
    <!-- Hero -->
    <div class="ctr-hero mb-1">
      <div class="ctr-hero__bg"/>
      <div class="ctr-hero__content">
        <div class="d-flex align-items-start justify-content-between flex-wrap" style="gap:12px">
          <div>
            <h3 class="ctr-hero__title mb-25">{{ msg('Contracts') }}</h3>
            <p class="ctr-hero__subtitle mb-0">{{ msg('Manage all service contracts') }}</p>
          </div>
          <b-button variant="light" size="sm" class="ctr-hero__new-btn" @click="showNewContractModal = true">
            <feather-icon icon="PlusIcon" size="15" class="mr-50"/>{{ msg('New Contract') }}
          </b-button>
        </div>
        <div class="d-flex flex-wrap mt-1" style="gap:10px">
          <div class="ctr-hero__stat">
            <feather-icon icon="FileTextIcon" size="14"/>
            <span class="font-weight-bolder">{{ stats.total }}</span> {{ msg('total') }}
          </div>
          <div class="ctr-hero__stat ctr-hero__stat--secondary">
            <feather-icon icon="EditIcon" size="14"/>
            <span class="font-weight-bolder">{{ stats.draft }}</span> {{ msg('drafts') }}
          </div>
          <div class="ctr-hero__stat ctr-hero__stat--info">
            <feather-icon icon="SendIcon" size="14"/>
            <span class="font-weight-bolder">{{ stats.sent }}</span> {{ msg('sent') }}
          </div>
          <div class="ctr-hero__stat ctr-hero__stat--success">
            <feather-icon icon="CheckCircleIcon" size="14"/>
            <span class="font-weight-bolder">{{ stats.signed }}</span> {{ msg('signed') }}
          </div>
          <div class="ctr-hero__stat ctr-hero__stat--danger">
            <feather-icon icon="XCircleIcon" size="14"/>
            <span class="font-weight-bolder">{{ stats.cancelled }}</span> {{ msg('cancelled') }}
          </div>
        </div>
      </div>
      <svg class="ctr-hero__illustration" viewBox="0 0 120 140" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="10" y="5" width="80" height="105" rx="6" fill="rgba(255,255,255,.15)" stroke="rgba(255,255,255,.25)" stroke-width="1.5"/>
        <rect x="22" y="22" width="36" height="3" rx="1.5" fill="rgba(255,255,255,.4)"/>
        <rect x="22" y="30" width="50" height="2.5" rx="1.25" fill="rgba(255,255,255,.25)"/>
        <rect x="22" y="38" width="44" height="2.5" rx="1.25" fill="rgba(255,255,255,.25)"/>
        <rect x="22" y="58" width="30" height="6" rx="3" fill="rgba(255,255,255,.3)"/>
        <path d="M60 70 l8 8 l16 -16" stroke="rgba(255,255,255,.4)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
        <circle cx="100" cy="25" r="10" fill="rgba(255,255,255,.08)" class="ctr-hero__float1"/>
        <circle cx="105" cy="90" r="7" fill="rgba(255,255,255,.06)" class="ctr-hero__float2"/>
      </svg>
    </div>

    <!-- Filters -->
    <b-card no-body class="mb-1">
      <b-card-body class="pb-0 pt-75">
        <b-row>
          <b-col cols="12" md="4" class="mb-75">
            <b-form-input v-model="search" size="sm" :placeholder="msg('Search contracts...')" debounce="300"/>
          </b-col>
          <b-col cols="6" md="3" class="mb-75">
            <v-select v-model="filterStatus" :options="statusOptions" :reduce="s=>s.value" label="label"
                      :placeholder="msg('All statuses')" :clearable="true" class="select-size-sm"/>
          </b-col>
          <b-col cols="6" md="3" class="mb-75">
            <v-select v-model="filterBranchId" :options="branchOptions" :reduce="b=>b.id" label="name"
                      :placeholder="msg('All branches')" :clearable="true" class="select-size-sm"/>
          </b-col>
          <b-col cols="12" md="2" class="mb-75 text-md-right">
            <b-button v-if="hasFilters" variant="flat-secondary" size="sm" @click="clearFilters">
              <feather-icon icon="XIcon" size="13" class="mr-25"/>{{ msg('Clear') }}
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Table -->
    <b-card no-body>
      <div class="m-1 d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center" style="gap:8px">
          <span class="text-muted small">{{ msg('Show') }}</span>
          <v-select v-model="perPage" :options="[10,25,50]" :clearable="false" class="per-page-selector" style="width:70px"/>
          <span class="text-muted small">{{ msg('entries') }}</span>
        </div>
      </div>

      <b-table
          ref="contractsTable"
          :items="contracts"
          :fields="tableFields"
          :busy="loading"
          responsive
          show-empty
          :empty-text="msg('No contracts found')"
          class="mb-0"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          @sort-changed="onSortChanged"
      >
        <template #table-busy>
          <div class="text-center my-2"><b-spinner variant="primary"/></div>
        </template>

        <template #cell(contractNumber)="data">
          <span class="font-weight-bold text-primary">{{ data.item.contractNumber }}</span>
        </template>

        <template #cell(contractType)="data">
          <b-badge :variant="typeVariant(data.item.contractType)">{{ contractTypeLabel(data.item.contractType) }}</b-badge>
        </template>

        <template #cell(clientCompanyName)="data">
          <div>
            <div class="font-weight-bold">{{ data.item.clientCompanyName }}</div>
            <small class="text-muted">{{ data.item.clientName }}</small>
          </div>
        </template>

        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)">{{ msg(data.item.status) }}</b-badge>
        </template>

        <template #cell(createdAt)="data">
          <span v-if="data.item.createdAt">{{ formatDate(data.item.createdAt) }}</span>
        </template>

        <template #cell(sentAt)="data">
          <span v-if="data.item.sentAt">{{ formatDate(data.item.sentAt) }}</span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(signedAt)="data">
          <span v-if="data.item.signedAt">{{ formatDate(data.item.signedAt) }}</span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(actions)="data">
          <div class="d-flex flex-nowrap" style="gap:4px">
            <b-button v-if="data.item.status !== 'SIGNED'" variant="outline-primary" size="sm" class="btn-icon" v-b-tooltip.hover :title="msg('Edit')"
                      @click="openEditor(data.item)">
              <feather-icon icon="EditIcon" size="14"/>
            </b-button>

            <b-button v-if="data.item.status === 'DRAFT'" variant="outline-info" size="sm" class="btn-icon"
                      v-b-tooltip.hover :title="msg('Send')" @click="onSend(data.item)">
              <feather-icon icon="SendIcon" size="14"/>
            </b-button>

            <b-button v-if="data.item.status === 'SENT'" variant="outline-info" size="sm" class="btn-icon"
                      v-b-tooltip.hover :title="msg('Resend')" @click="onResend(data.item)">
              <feather-icon icon="RefreshCwIcon" size="14"/>
            </b-button>

            <b-button v-if="data.item.status === 'SENT'" variant="outline-success" size="sm" class="btn-icon"
                      v-b-tooltip.hover :title="msg('Upload signed')" @click="openUpload(data.item)">
              <feather-icon icon="UploadIcon" size="14"/>
            </b-button>

            <b-button variant="outline-secondary" size="sm" class="btn-icon"
                      v-b-tooltip.hover :title="msg('Download PDF')" @click="onDownload(data.item)">
              <feather-icon icon="DownloadIcon" size="14"/>
            </b-button>

            <b-button v-if="data.item.signedPdfPath" variant="outline-success" size="sm" class="btn-icon"
                      v-b-tooltip.hover :title="msg('Download signed')" @click="onDownloadSigned(data.item)">
              <feather-icon icon="FileIcon" size="14"/>
            </b-button>

            <b-button v-if="data.item.status !== 'CANCELLED' && data.item.status !== 'SIGNED'"
                      variant="outline-warning" size="sm" class="btn-icon"
                      v-b-tooltip.hover :title="msg('Cancel')" @click="onCancel(data.item)">
              <feather-icon icon="SlashIcon" size="14"/>
            </b-button>
          </div>
        </template>
      </b-table>

      <div class="mx-1 my-1 d-flex align-items-center justify-content-between">
        <span class="text-muted small">
          {{ msg('Showing') }} {{ ((currentPage - 1) * perPage) + 1 }} {{ msg('to') }}
          {{ Math.min(currentPage * perPage, totalRows) }} {{ msg('of') }} {{ totalRows }} {{ msg('entries') }}
        </span>
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" first-number last-number
                      prev-class="prev-item" next-class="next-item" class="mb-0"/>
      </div>
    </b-card>

    <!-- New Contract Modal -->
    <b-modal v-model="showNewContractModal" centered :title="msg('New Contract')" hide-footer>
      <b-form-group :label="msg('Select Branch')" label-for="new-ctr-branch">
        <v-select
            id="new-ctr-branch"
            v-model="newContractBranchId"
            :options="branchOptions"
            :reduce="b => b.id"
            label="name"
            :placeholder="msg('Choose a branch...')"
        />
      </b-form-group>
      <b-form-group :label="msg('Language')" label-for="new-ctr-lang">
        <b-form-select id="new-ctr-lang" v-model="newContractLanguage"
                       :options="[{ value: 'EN', text: 'English' }, { value: 'DE', text: 'Deutsch' }]"/>
      </b-form-group>
      <b-form-group :label="msg('Contract Type')">
        <b-form-radio-group v-model="newContractMode" stacked>
          <b-form-radio value="all">{{ msg('Create all 3 contracts') }} <small class="text-muted">({{ msg('Service Agreement') }}, {{ msg('Legal Terms') }}, {{ msg('General Agreement') }})</small></b-form-radio>
          <b-form-radio value="single">{{ msg('Create single contract') }}</b-form-radio>
        </b-form-radio-group>
      </b-form-group>
      <b-form-group v-if="newContractMode === 'single'" :label="msg('Type')">
        <b-form-select v-model="newContractType" :options="contractTypeOptions"/>
      </b-form-group>
      <div class="d-flex justify-content-end" style="gap:8px">
        <b-button variant="secondary" size="sm" @click="showNewContractModal = false">{{ msg('Cancel') }}</b-button>
        <b-button variant="primary" size="sm" :disabled="!newContractBranchId || creatingContract" @click="createNewContract">
          <b-spinner v-if="creatingContract" small class="mr-50"/>
          <feather-icon v-else icon="PlusIcon" size="14" class="mr-25"/>
          {{ newContractMode === 'all' ? msg('Create All Contracts') : msg('Create Contract') }}
        </b-button>
      </div>
    </b-modal>

    <!-- Editor Modal -->
    <contract-editor-modal ref="editorModal" @saved="loadContracts" @sent="loadContracts"/>
    <!-- Upload Modal -->
    <upload-signed-modal ref="uploadModal" @uploaded="loadContracts"/>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import moment from 'moment'
import ContractEditorModal from '@/components/modals/ContractEditorModal.vue'
import UploadSignedModal from '@/components/modals/UploadSignedModal.vue'

export default {
  name: 'Contracts',
  components: { vSelect, ContractEditorModal, UploadSignedModal },
  data() {
    return {
      search: '',
      filterStatus: null,
      filterBranchId: null,
      perPage: 10,
      currentPage: 1,
      sortBy: 'createdAt',
      sortDesc: true,
      showNewContractModal: false,
      newContractBranchId: null,
      newContractLanguage: 'EN',
      newContractMode: 'all',
      newContractType: 'MANAGEMENT',
      creatingContract: false,
    }
  },
  computed: {
    loading() { return this.$store.getters['contract/isLoading'] },
    contracts() { return this.$store.getters['contract/getContracts'] },
    totalRows() { return this.$store.getters['contract/getTotal'] },
    branchOptions() {
      return Object.values(this.$store.getters['data/getAllBranches'] || {})
    },
    contractTypeOptions() {
      return [
        { value: 'MANAGEMENT', text: this.msg('Service Agreement') },
        { value: 'LEGAL', text: this.msg('Legal Terms') },
        { value: 'GENERAL', text: this.msg('General Agreement') },
      ]
    },
    statusOptions() {
      return [
        { value: 'DRAFT', label: this.msg('DRAFT') },
        { value: 'SENT', label: this.msg('SENT') },
        { value: 'SIGNED', label: this.msg('SIGNED') },
        { value: 'CANCELLED', label: this.msg('CANCELLED') },
      ]
    },
    hasFilters() {
      return this.search || this.filterStatus || this.filterBranchId
    },
    stats() {
      let total = 0, draft = 0, sent = 0, signed = 0, cancelled = 0
      const all = this.contracts || []
      total = this.totalRows || all.length
      all.forEach(c => {
        if (c.status === 'DRAFT') draft++
        else if (c.status === 'SENT') sent++
        else if (c.status === 'SIGNED') signed++
        else if (c.status === 'CANCELLED') cancelled++
      })
      return { total, draft, sent, signed, cancelled }
    },
    tableFields() {
      return [
        { key: 'contractNumber', label: this.msg('Contract #'), sortable: true },
        { key: 'contractType', label: this.msg('Type'), sortable: true },
        { key: 'clientCompanyName', label: this.msg('Client'), sortable: true },
        { key: 'status', label: this.msg('Status'), sortable: true },
        { key: 'language', label: this.msg('Language'), sortable: false },
        { key: 'createdAt', label: this.msg('Created'), sortable: true },
        { key: 'sentAt', label: this.msg('Sent'), sortable: true },
        { key: 'signedAt', label: this.msg('Signed'), sortable: true },
        { key: 'actions', label: this.msg('Actions'), sortable: false, thClass: 'text-right', tdClass: 'text-right' },
      ]
    },
  },
  watch: {
    search() { this.currentPage = 1; this.loadContracts() },
    filterStatus() { this.currentPage = 1; this.loadContracts() },
    filterBranchId() { this.currentPage = 1; this.loadContracts() },
    perPage() { this.currentPage = 1; this.loadContracts() },
    currentPage() { this.loadContracts() },
  },
  mounted() {
    this.loadContracts()
  },
  methods: {
    loadContracts() {
      const filter = { search: this.search || undefined }
      if (this.filterStatus) {
        filter.eq = { status: this.filterStatus }
      }
      if (this.filterBranchId) {
        if (!filter.eq) filter.eq = {}
        filter.eq.branchId = this.filterBranchId
      }
      this.$store.dispatch('contract/getContracts', {
        page: this.currentPage,
        amount: this.perPage,
        filter,
        sortcolumn: this.sortBy,
        sortby: this.sortDesc ? 'desc' : 'asc'
      })
    },
    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.loadContracts()
    },
    statusVariant(status) {
      const map = { DRAFT: 'light-secondary', SENT: 'light-info', SIGNED: 'light-success', CANCELLED: 'light-danger' }
      return map[status] || 'light-secondary'
    },
    typeVariant(type) {
      const map = { MANAGEMENT: 'light-primary', LEGAL: 'light-info', GENERAL: 'light-success' }
      return map[(type || '').toUpperCase()] || 'light-secondary'
    },
    contractTypeLabel(type) {
      const map = {
        MANAGEMENT: this.msg('Service Agreement'),
        LEGAL: this.msg('Legal Terms'),
        GENERAL: this.msg('General Agreement'),
      }
      return map[(type || '').toUpperCase()] || type
    },
    formatDate(d) {
      return moment(d).format('DD.MM.YYYY HH:mm')
    },
    clearFilters() {
      this.search = ''
      this.filterStatus = null
      this.filterBranchId = null
    },
    openEditor(contract) {
      this.$refs.editorModal.open(contract)
    },
    openUpload(contract) {
      this.$refs.uploadModal.open(contract)
    },
    async onSend(contract) {
      const ok = await this.$bvModal.msgBoxConfirm(
          this.msg('Send this contract by email?'),
          { title: this.msg('Confirm Send'), okVariant: 'primary', okTitle: this.msg('Send'), cancelTitle: this.msg('Cancel') }
      )
      if (!ok) return
      await this.$store.dispatch('contract/sendContract', { id: contract.id })
      this.loadContracts()
    },
    async onResend(contract) {
      const ok = await this.$bvModal.msgBoxConfirm(
          this.msg('Resend this contract by email?'),
          { title: this.msg('Confirm Resend'), okVariant: 'info', okTitle: this.msg('Resend'), cancelTitle: this.msg('Cancel') }
      )
      if (!ok) return
      await this.$store.dispatch('contract/resendContract', { id: contract.id })
      this.loadContracts()
    },
    async onCancel(contract) {
      const ok = await this.$bvModal.msgBoxConfirm(
          this.msg('Cancel this contract? This action cannot be undone.'),
          { title: this.msg('Confirm Cancel'), okVariant: 'warning', okTitle: this.msg('Yes, cancel'), cancelTitle: this.msg('No') }
      )
      if (!ok) return
      await this.$store.dispatch('contract/cancelContract', { id: contract.id })
      this.loadContracts()
    },
    onDownload(contract) {
      this.$store.dispatch('contract/downloadPdf', { id: contract.id })
    },
    onDownloadSigned(contract) {
      this.$store.dispatch('contract/downloadSignedPdf', { id: contract.id })
    },
    async createNewContract() {
      if (!this.newContractBranchId) return
      this.creatingContract = true
      try {
        if (this.newContractMode === 'all') {
          await this.$store.dispatch('contract/createAllContracts', {
            branchId: this.newContractBranchId,
            leadId: null,
            language: this.newContractLanguage,
          })
          this.showNewContractModal = false
          this.newContractBranchId = null
          this.newContractLanguage = 'EN'
          this.newContractMode = 'all'
          this.loadContracts()
        } else {
          const ans = await this.$store.dispatch('contract/createContract', {
            branchId: this.newContractBranchId,
            leadId: null,
            language: this.newContractLanguage,
            contractType: this.newContractType,
          })
          this.showNewContractModal = false
          this.newContractBranchId = null
          this.newContractLanguage = 'EN'
          this.newContractMode = 'all'
          this.loadContracts()
          if (ans && ans.Contract) {
            this.$nextTick(() => this.openEditor(ans.Contract))
          }
        }
      } finally {
        this.creatingContract = false
      }
    },
  }
}
</script>

<style lang="scss" scoped>
/* ─── Hero ─── */
.ctr-hero {
  position: relative; border-radius: 12px; overflow: hidden; padding: 24px 28px; min-height: 120px;
  &__bg { position: absolute; inset: 0; background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #a855f7 100%); z-index: 0; }
  &__content { position: relative; z-index: 1; color: #fff; }
  &__title { font-size: 1.4rem; font-weight: 700; color: #fff; }
  &__subtitle { font-size: .9rem; opacity: .85; color: #fff; }
  &__stat {
    display: inline-flex; align-items: center; gap: 5px; background: rgba(255,255,255,.18);
    padding: 4px 12px; border-radius: 20px; font-size: .8rem; color: #fff; backdrop-filter: blur(4px);
    &--secondary { background: rgba(255,255,255,.12); }
    &--info { background: rgba(59,130,246,.35); }
    &--success { background: rgba(16,185,129,.35); }
    &--danger { background: rgba(239,68,68,.3); }
  }
  &__illustration {
    position: absolute; right: 20px; top: 50%; transform: translateY(-50%); width: 100px; opacity: .5;
    @media(max-width:768px){ display: none; }
  }
  &__float1 { animation: ctrFloat 4s ease-in-out infinite; }
  &__float2 { animation: ctrFloat 5s ease-in-out 1s infinite; }
}

@keyframes ctrFloat {
  0%,100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

.ctr-hero__new-btn {
  background: rgba(255,255,255,.22); border: 1px solid rgba(255,255,255,.35);
  color: #fff; font-weight: 600; backdrop-filter: blur(4px);
  &:hover { background: rgba(255,255,255,.35); color: #fff; border-color: rgba(255,255,255,.5); }
}

.dark-layout .ctr-hero__bg {
  background: linear-gradient(135deg, #312e81 0%, #4c1d95 50%, #6b21a8 100%);
}
</style>
